import { Alert, Button, Card, Spin, Typography } from 'antd'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { setAuthToken } from '../../lib/api'
import { exchangeProxySession } from '../../lib/auth-service'
import { storeProxySessionAuth } from '../../lib/auth-storage'
import { extractUserFacingMessage } from '../../providers/sdkErrorUtils'
import { loginStyles } from '../login/loginStyles'

const { Title, Paragraph } = Typography

type ProxyAccessState =
  | { kind: 'working' }
  | { kind: 'ended' }
  | { kind: 'failed'; message: string }

/** Reads the one-time code from the fragment and wipes it from the address bar and history. */
function takeCodeFromLocation(): { code: string | null; ended: boolean } {
  const code = new URLSearchParams(globalThis.location.hash.slice(1)).get('code')
  const ended = new URLSearchParams(globalThis.location.search).get('ended') === '1'
  globalThis.history.replaceState(
    null,
    '',
    globalThis.location.pathname + (ended ? '?ended=1' : ''),
  )
  return { code, ended }
}

/**
 * 슈퍼관리자 대행 접속 (#273) — the tab the backoffice user detail page opened. It trades the
 * one-time code for this tab's session and goes to the dashboard; with `?ended=1` it says the
 * session is over (a proxy tab cannot sign in again by itself).
 */
export function ProxyAccess() {
  const { t } = useTranslation()
  const started = useRef(false)
  const [state, setState] = useState<ProxyAccessState>({ kind: 'working' })

  useEffect(() => {
    // The code works once — StrictMode's second effect run must not spend it again.
    if (started.current) return
    started.current = true
    try {
      // The backoffice tab that opened this one needs no handle on it.
      globalThis.opener = null
    } catch {
      // Some browsers make `opener` read-only; nothing to do.
    }
    const { code, ended } = takeCodeFromLocation()
    if (!code) {
      setState(ended ? { kind: 'ended' } : { kind: 'failed', message: t('auth.proxy.noCode') })
      return
    }
    exchangeProxySession(code)
      .then((access) => {
        storeProxySessionAuth(access.accessToken, access.user, access.expiresIn, access.session)
        setAuthToken(access.accessToken)
        // A full load so the auth provider and every screen start from this tab's session.
        globalThis.location.replace('/')
      })
      .catch((error_: unknown) => {
        setState({
          kind: 'failed',
          message: extractUserFacingMessage(error_) ?? t('auth.proxy.failed'),
        })
      })
  }, [t])

  return (
    <div style={loginStyles.container}>
      <Card style={loginStyles.card}>
        <div style={loginStyles.header}>
          <img src="/boltb-logo.png" alt="BoltB" style={loginStyles.logo} />
          <Title level={3} style={loginStyles.title}>
            {t('auth.proxy.title')}
          </Title>
        </div>
        {state.kind === 'working' && (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
            <Paragraph style={{ marginTop: 16 }}>{t('auth.proxy.working')}</Paragraph>
          </div>
        )}
        {state.kind === 'ended' && <Alert type="info" showIcon message={t('auth.proxy.ended')} />}
        {state.kind === 'failed' && <Alert type="error" showIcon message={state.message} />}
        {state.kind !== 'working' && (
          <>
            <Paragraph type="secondary" style={{ marginTop: 16 }}>
              {t('auth.proxy.reopenHint')}
            </Paragraph>
            <Button block onClick={() => globalThis.close()}>
              {t('auth.proxy.closeTab')}
            </Button>
          </>
        )}
      </Card>
    </div>
  )
}
